import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, ChevronRight, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import ClientProfileDialog from '@/components/ClientProfileDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (Number.isNaN(diff)) return '';
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

/**
 * NotificationBell — a compact bell icon with an unread-count badge that opens
 * a dropdown of recent in-app notifications. Polls /api/notifications every
 * minute when mounted (cheap because the route is indexed).
 *
 * Click on a row → marks it read and navigates to its `link` (deep link into
 * the corresponding dashboard surface). "Mark all read" clears the badge.
 */
const NotificationBell = ({ className = '', testIdPrefix = 'notif' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notifications?limit=20`, {
        withCredentials: true,
      });
      setItems(res.data?.items || []);
      setUnreadCount(res.data?.unread_count || 0);
    } catch {
      // Silent — bell stays at last known state.
    }
  }, []);

  useEffect(() => {
    fetchFeed();

    // Server-Sent Events stream — instant push instead of 60s polling. The
    // browser's EventSource auto-reconnects with exponential backoff if the
    // connection drops, so we don't need our own retry loop. Polling is kept
    // as a defense-in-depth safety net (5min) in case SSE is blocked by a
    // proxy / network policy.
    let eventSource = null;
    try {
      eventSource = new EventSource(
        `${BACKEND_URL}/api/notifications/stream`,
        { withCredentials: true },
      );
      eventSource.addEventListener('notification', (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data?.kind === 'notification.created' && data?.notification) {
            // Prepend new notification, bump unread count.
            setItems((prev) => {
              const next = [data.notification, ...prev];
              return next.slice(0, 20);
            });
            setUnreadCount((c) => c + 1);
          }
        } catch {
          // Bad payload — ignore.
        }
      });
    } catch {
      // EventSource unavailable — polling fallback handles it.
    }

    const interval = setInterval(fetchFeed, 5 * 60 * 1000);
    return () => {
      clearInterval(interval);
      try { eventSource && eventSource.close(); } catch { /* noop */ }
    };
  }, [fetchFeed]);

  // Refetch when the dropdown opens so the user sees fresh data.
  useEffect(() => { if (open) fetchFeed(); }, [open, fetchFeed]);

  // Outside-click close.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleRowClick = async (item) => {
    setOpen(false);
    if (!item.read_at) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/notifications/${item.notification_id}/read`,
          {},
          { withCredentials: true },
        );
        // Optimistic local update — bell badge drops by one.
        setItems((prev) => prev.map((n) =>
          n.notification_id === item.notification_id ? { ...n, read_at: new Date().toISOString() } : n
        ));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // Non-fatal — link nav still happens.
      }
    }
    if (item.link) navigate(item.link);
  };

  // Ticket-related events get a secondary "View client" deep-link → opens
  // the aggregated ClientProfileDialog for the ticket owner so admins can
  // jump straight into a client's full history from the bell.
  const [profileUserId, setProfileUserId] = useState(null);
  const openClientProfileFor = async (notification) => {
    if (!notification?.related_id) return;
    try {
      // The notification carries the ticket_id; resolve the user via the
      // public ticket endpoint (admin-auth, returns owner user_id).
      const res = await axios.get(
        `${BACKEND_URL}/api/tickets/${notification.related_id}`,
        { withCredentials: true },
      );
      if (res.data?.user_id) {
        setProfileUserId(res.data.user_id);
        setOpen(false);
      }
    } catch {
      toast.error('Failed to open client profile');
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/notifications/mark-all-read`,
        {},
        { withCredentials: true },
      );
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || now })));
      setUnreadCount(0);
      toast.success('All notifications marked read');
    } catch {
      toast.error('Could not mark all read');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef} data-testid={`${testIdPrefix}-bell-wrap`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded hover:bg-muted/40 transition-colors"
        data-testid={`${testIdPrefix}-bell-btn`}
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center"
            data-testid={`${testIdPrefix}-bell-badge`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-card border border-border rounded-lg shadow-2xl z-50 overflow-hidden"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <p className="text-xs uppercase tracking-widest font-bold">Notifications</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllRead}
              disabled={loading || unreadCount === 0}
              className="text-[10px] uppercase tracking-widest"
              data-testid={`${testIdPrefix}-mark-all-read`}
            >
              <Check className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
                You're all caught up.
              </div>
            ) : (
              items.map((n) => (
                <button
                  type="button"
                  key={n.notification_id}
                  onClick={() => handleRowClick(n)}
                  data-testid={`${testIdPrefix}-row-${n.notification_id}`}
                  className={
                    'w-full text-left px-4 py-3 flex items-start gap-3 border-b border-border/40 hover:bg-muted/20 transition-colors ' +
                    (!n.read_at ? 'bg-primary/5' : '')
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {!n.read_at && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                      <p className={'text-sm leading-snug ' + (!n.read_at ? 'font-bold' : 'text-muted-foreground')}>
                        {n.title}
                      </p>
                    </div>
                    {n.body && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.body}</p>
                    )}
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 mt-1">
                      {fmtRelative(n.created_at)}
                    </p>
                    {(n.event_type || '').startsWith('ticket') && n.related_id && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openClientProfileFor(n); }}
                        className="text-[10px] text-primary hover:underline mt-1 inline-flex items-center gap-1"
                        data-testid={`bell-open-profile-${n.notification_id}`}
                      >
                        <UserIcon className="w-3 h-3" />Open client profile
                      </button>
                    )}
                  </div>
                  {n.link && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <ClientProfileDialog
        userId={profileUserId}
        open={!!profileUserId}
        onClose={() => setProfileUserId(null)}
      />
    </div>
  );
};

export default NotificationBell;
